import { Suspense, lazy } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard, SuperAdminGuard } from 'src/auth/guard';
import { CROSS_ORG_ROLES } from 'src/utils/roles';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
// sections
import NationalTabs from 'src/sections/national/national-tabs';
import { NationalProvider } from 'src/sections/national/national-provider';

/**
 * National tracking console.
 *
 * Superadmin and national_viewer only, enforced in TWO independent places on purpose:
 *
 *  - here, so anyone else who types /national is redirected rather than
 *    landing on a page that renders empty and throws 403s from every call;
 *  - and server-side, via roleCheck(['superadmin', 'national_viewer']) on /api/national, which is
 *    the one that actually matters. This guard is a UX affordance, not access
 *    control — the API must refuse the request regardless of what the client
 *    chooses to render.
 *
 * The section spans every organisation's voters, so the usual per-org scoping
 * does not narrow it and the role check is not optional.
 */
const DistrictOverviewPage = lazy(() => import('src/pages/national/district-overview'));
const PriorityDistrictsPage = lazy(() => import('src/pages/national/priority-districts'));
const FieldContactPage = lazy(() => import('src/pages/national/field-contact'));

/**
 * NationalProvider is mounted HERE, above the outlet, not inside the pages.
 *
 * It holds the selected scope and the one-shot /coverage fetch. Mounted per
 * page, every tab switch would remount it — refetching coverage and silently
 * resetting the user's selected state each time they moved between tabs.
 * Wrapping the outlet makes it outlive the tab, which is the only reason the
 * two screens can share a scope at all.
 */
export const nationalRoutes = [
  {
    path: 'national',
    element: (
      <AuthGuard>
        <SuperAdminGuard roles={CROSS_ORG_ROLES}>
          <DashboardLayout>
            <NationalProvider>
              <NationalTabs />
              <Suspense fallback={<LoadingScreen />}>
                <Outlet />
              </Suspense>
            </NationalProvider>
          </DashboardLayout>
        </SuperAdminGuard>
      </AuthGuard>
    ),
    children: [
      { index: true, element: <DistrictOverviewPage /> },
      { path: 'district-overview', element: <DistrictOverviewPage /> },
      { path: 'priority-districts', element: <PriorityDistrictsPage /> },
      { path: 'field-contact', element: <FieldContactPage /> },
    ],
  },
];
